import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Box, ClickAwayListener, Fade, Paper, Popper, TextField } from '@mui/material';
import { Action } from '@reduxjs/toolkit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

type Props = {
    initialDiscount?: number;
    open: boolean;
    canWrite: boolean;
    maxDiscount: number;
    title?: string;
    saveDiscountActionCreator(saveDiscountActionCreator: number | null): Action;
    setOpen(open: boolean): void;
};

function parseDiscount(discountStr: string, maxDiscount: number) {
    if (discountStr.length === 0) {
        return 0;
    }

    return Math.min(maxDiscount, Number(discountStr.replace(',', '.')));
}

function handleOnClick(e: React.MouseEvent) {
    e.stopPropagation();
}

export const DiscountPopperButton = ({
    initialDiscount,
    saveDiscountActionCreator,
    open,
    setOpen,
    canWrite,
    maxDiscount,
    title
}: Props) => {
    const [discount, setDiscount] = useState('');
    const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);
    const anchorRef = useRef<SVGSVGElement>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        setDiscount(initialDiscount != null ? String(initialDiscount) : '');
    }, [initialDiscount]);

    const resetDiscount = useCallback(() => {
        setDiscount(initialDiscount != null ? String(initialDiscount) : '');
    }, [initialDiscount, setDiscount]);

    const handleToggle = useCallback(() => {
        setOpen(!open);
    }, [open, setOpen]);

    const handleClose = useCallback(
        (event: globalThis.MouseEvent | TouchEvent) => {
            const target = event.target as HTMLElement;
            if (!anchorRef.current?.contains(target)) {
                if (canWrite) {
                    const discountNumber = parseDiscount(discount, maxDiscount);
                    if (discountNumber !== initialDiscount) {
                        if (discountNumber >= 0) {
                            dispatch(saveDiscountActionCreator(discountNumber));
                        } else {
                            resetDiscount();
                        }
                    }
                }

                setOpen(false);
            }
        },
        [
            canWrite,
            initialDiscount,
            discount,
            resetDiscount,
            setOpen,
            dispatch,
            saveDiscountActionCreator,
            maxDiscount
        ]
    );

    const handleSetDiscount = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (canWrite) {
                setDiscount(event.target.value);
            }
        },
        [canWrite, setDiscount]
    );

    const handleOnKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                const discountNumber = parseDiscount(discount, maxDiscount);
                if (discountNumber !== initialDiscount) {
                    if (discountNumber >= 0) {
                        dispatch(saveDiscountActionCreator(discountNumber));
                    } else {
                        resetDiscount();
                    }
                }
                setOpen(false);
            }

            if (e.key === 'Escape') {
                setOpen(false);
                resetDiscount();
            }

            e.stopPropagation();
        },
        [discount, initialDiscount, saveDiscountActionCreator, setOpen, resetDiscount, dispatch, maxDiscount]
    );

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select();
    };

    return (
        <Box sx={{ display: 'flex', padding: '2px' }}>
            <LocalOfferIcon
                aria-haspopup="true"
                onClick={handleToggle}
                opacity={0.5}
                fontSize="small"
                ref={anchorRef}
                sx={{
                    color:
                        initialDiscount != null && initialDiscount > 0
                            ? 'rgba(0, 39, 255, 1)'
                            : 'rgba(63, 81, 181, 0.42)',
                    fontSize: initialDiscount != null && initialDiscount > 0 ? '1rem' : undefined
                }}
            />

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom"
                sx={theme => ({
                    zIndex: 2,
                    '&[x-placement*="top"] $arrow': {
                        bottom: 0,
                        left: 0,
                        marginBottom: '-0.9em',
                        width: '3em',
                        height: '1em',
                        '&::before': {
                            borderWidth: '1em 1em 0 1em',
                            borderColor: `${theme.palette.background.paper} transparent transparent transparent`
                        }
                    },
                    '&[x-placement*="bottom"] $arrow': {
                        top: 0,
                        left: 0,
                        marginTop: '-0.9em',
                        width: '3em',
                        height: '1em',
                        '&::before': {
                            borderWidth: '0 1em 1em 1em',
                            borderColor: `transparent transparent ${theme.palette.background.paper} transparent`
                        }
                    }
                })}
                modifiers={[
                    {
                        name: 'arrow',
                        enabled: true,
                        options: {
                            element: arrowRef
                        }
                    }
                ]}
                transition
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={event => handleClose(event)}>
                        <Fade {...TransitionProps} timeout={350}>
                            <Box component="div">
                                <Box
                                    component="span"
                                    sx={{
                                        position: 'absolute',
                                        fontSize: 7,
                                        width: '3em',
                                        height: '3em',
                                        '&::before': {
                                            content: '""',
                                            margin: 'auto',
                                            display: 'block',
                                            width: 0,
                                            height: 0,
                                            borderStyle: 'solid'
                                        }
                                    }}
                                    ref={setArrowRef}
                                />
                                <Paper sx={{ width: '80px', padding: '8px' }}>
                                    <TextField
                                        label={title ?? 'Discount'}
                                        onChange={handleSetDiscount}
                                        value={discount ?? ''}
                                        autoFocus
                                        onKeyDown={handleOnKeyDown}
                                        variant="standard"
                                        inputProps={{ maxLength: 100 }}
                                        fullWidth={true}
                                        onClick={handleOnClick}
                                        onDoubleClick={handleOnClick}
                                        disabled={!canWrite}
                                        onFocus={handleFocus}
                                    />
                                </Paper>
                            </Box>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </Box>
    );
};
